const BROWSER_TIME_ZONE =
    (Intl.DateTimeFormat().resolvedOptions().timeZone &&
        Intl.DateTimeFormat().resolvedOptions().timeZone.trim()) ||
    'UTC';

/**
 * Resolve a preferred time zone into a valid IANA identifier.
 * Falls back to the browser zone (or UTC) when invalid/empty, and treats
 * explicit UTC responses as "unspecified" so the browser zone wins.
 */
export const resolveTimeZone = raw => {
    const candidate = typeof raw === 'string' ? raw.trim() : '';
    const normalized = candidate.toUpperCase();
    const prefersBrowser = normalized === 'UTC' || normalized === 'ETC/UTC';
    if (!candidate || prefersBrowser) {
        return BROWSER_TIME_ZONE;
    }
    try {
        // Throws for invalid IANA ids
        new Intl.DateTimeFormat('en-US', { timeZone: candidate });
        return candidate;
    } catch (error) {
        console.warn(
            'Invalid time zone received, falling back to browser time zone',
            candidate,
            error
        );
        return BROWSER_TIME_ZONE;
    }
};

const formatterCache = new Map();

const getDateTimeFormatter = timeZone => {
    const tz = resolveTimeZone(timeZone);
    if (formatterCache.has(tz)) {
        return formatterCache.get(tz);
    }
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
    formatterCache.set(tz, formatter);
    return formatter;
};

/**
 * Get the date-time parts for a UTC timestamp in the specified time zone.
 */
export const getZonedDateTimeParts = (utcMs, timeZone) => {
    const parts = {};
    getDateTimeFormatter(timeZone)
        .formatToParts(new Date(utcMs))
        .forEach(({ type, value }) => {
            if (type !== 'literal') {
                parts[type] = value;
            }
        });

    const safeNumber = val => {
        const num = Number(val);
        return Number.isFinite(num) ? num : 0;
    };

    return {
        year: safeNumber(parts.year),
        month: safeNumber(parts.month),
        day: safeNumber(parts.day),
        hour: safeNumber(parts.hour),
        minute: safeNumber(parts.minute),
        second: safeNumber(parts.second),
    };
};

/**
 * Calculate the offset (in minutes) for a UTC timestamp in the specified time zone.
 */
export const getTimeZoneOffsetMinutes = (utcMs, timeZone) => {
    const { year, month, day, hour, minute, second } = getZonedDateTimeParts(
        utcMs,
        timeZone
    );
    const asUtc = Date.UTC(year, month - 1, day, hour, minute, second, 0);
    return (asUtc - utcMs) / 60000;
};

/**
 * Convert a wall-clock time in a time zone to a UTC timestamp.
 */
export const zonedTimeToUtc = (dateParts = {}, timeZone) => {
    const {
        year,
        month,
        day,
        hour = 0,
        minute = 0,
        second = 0,
        millisecond = 0,
    } = dateParts;
    if (
        !Number.isFinite(year) ||
        !Number.isFinite(month) ||
        !Number.isFinite(day)
    ) {
        return NaN;
    }
    const guess = Date.UTC(
        year,
        month - 1,
        day,
        hour,
        minute,
        second,
        millisecond
    );
    const offsetMinutes = getTimeZoneOffsetMinutes(guess, timeZone);
    return guess - offsetMinutes * 60 * 1000;
};

/**
 * Format a date/time value in the specified time zone.
 */
export const formatDateTimeInZone = (value, timeZone, options = {}) => {
    if (value === null || value === undefined) {
        return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    const formatter = new Intl.DateTimeFormat(undefined, {
        timeZone: resolveTimeZone(timeZone),
        ...options,
    });
    return formatter.format(date);
};
